import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import {
  getCapsuleById,
  updateCapsuleContent,
  withdrawCapsule,
  getIncubationRelatedCapsules,
} from '../lib/supabase'
import './IncubationWorkspace.css'

const REFLECTION_PROMPTS = [
  'What would the strongest version of the opposing argument say?',
  'Is this claim falsifiable?',
  'What evidence would change your mind?',
  'Which part of this post is emotional vs analytical?',
  'Would you phrase this differently if the audience disagreed?',
]

const RESEARCH_LINKS_BY_TOPIC = {
  Philosophy: [
    { title: 'Stanford Encyclopedia of Philosophy', url: 'https://plato.stanford.edu/' },
    { title: 'Internet Encyclopedia of Philosophy', url: 'https://iep.utm.edu/' },
  ],
  Economics: [
    { title: 'NBER Working Papers', url: 'https://www.nber.org/papers' },
    { title: 'Our World in Data', url: 'https://ourworldindata.org/' },
  ],
  Politics: [
    { title: 'Pew Research Center', url: 'https://www.pewresearch.org/' },
    { title: 'Brookings Institution', url: 'https://www.brookings.edu/' },
  ],
  Science: [
    { title: 'Google Scholar', url: 'https://scholar.google.com/' },
    { title: 'arXiv', url: 'https://arxiv.org/' },
  ],
  Culture: [
    { title: 'JSTOR Open Content', url: 'https://about.jstor.org/oa-and-free/' },
    { title: 'SAGE Journals', url: 'https://journals.sagepub.com/' },
  ],
  Technology: [
    { title: 'ACM Digital Library', url: 'https://dl.acm.org/' },
    { title: 'IEEE Xplore', url: 'https://ieeexplore.ieee.org/' },
  ],
}

function splitSentences(text = '') {
  return text
    .split(/(?<=[.!?])\s+/)
    .map(s => s.trim())
    .filter(Boolean)
}

function changedSentences(current, previous) {
  const previousSet = new Set(splitSentences(previous))
  return splitSentences(current).filter(sentence => !previousSet.has(sentence))
}

function getCountdownLabel(publishesAt) {
  if (!publishesAt) return ''
  const diff = new Date(publishesAt) - new Date()
  if (diff <= 0) return 'Publishing…'
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  const s = Math.floor((diff % 60000) / 1000)
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function buildCounterarguments(content) {
  const claimLine = content
    .split('\n')
    .find(line => line.toLowerCase().startsWith('claim:'))
    ?.replace(/^claim:\s*/i, '')
    .trim()

  const claim = claimLine || content.split('\n')[0]?.slice(0, 180) || 'your current thesis'

  return [
    `Strong opposing case: what if the best objection is that "${claim}" overstates certainty from limited evidence?`,
    'Falsifiability check: name one measurable outcome that would disconfirm your argument within a specific timeframe.',
    'Audience reversal test: rewrite one paragraph as if you were persuading a skeptical reader, not an ally.',
  ]
}

export default function IncubationWorkspace() {
  const { capsuleId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [capsule, setCapsule] = useState(null)
  const [draft, setDraft] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [timeLeft, setTimeLeft] = useState('')
  const [promptIndex, setPromptIndex] = useState(0)
  const [relatedCapsules, setRelatedCapsules] = useState([])

  useEffect(() => {
    const id = setInterval(() => {
      setPromptIndex(p => (p + 1) % REFLECTION_PROMPTS.length)
    }, 12000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const data = await getCapsuleById(capsuleId)
        if (cancelled) return
        if (!data) {
          setError('Capsule not found.')
          return
        }

        if (data.author_id !== user?.id) {
          navigate(`/capsule/${capsuleId}`)
          return
        }

        setCapsule(data)
        setDraft(data.content || '')

        const related = await getIncubationRelatedCapsules(data, 5)
        if (!cancelled) setRelatedCapsules(related)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load incubation workspace.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    if (user && capsuleId) load()
    return () => { cancelled = true }
  }, [capsuleId, user, navigate])

  useEffect(() => {
    if (!capsule?.publishes_at) return
    const update = () => setTimeLeft(getCountdownLabel(capsule.publishes_at))
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [capsule?.publishes_at])

  const iterations = capsule?.iterations || []
  const lastVersionContent = iterations.length > 0 ? iterations[iterations.length - 1].content || '' : ''

  const changed = useMemo(() => changedSentences(draft, lastVersionContent), [draft, lastVersionContent])
  const counterarguments = useMemo(() => buildCounterarguments(draft), [draft])

  const researchLinks = useMemo(() => {
    const tag = capsule?.tags?.[0]
    return RESEARCH_LINKS_BY_TOPIC[tag] || [
      { title: 'Google Scholar', url: 'https://scholar.google.com/' },
      { title: 'Our World in Data', url: 'https://ourworldindata.org/' },
    ]
  }, [capsule?.tags])

  const hasMeaningfulChange = draft.trim() && draft.trim() !== (capsule?.content || '').trim()

  const saveDraft = async () => {
    if (!capsule || !user || !hasMeaningfulChange) return
    setSaving(true)
    setError('')
    try {
      const updated = await updateCapsuleContent(capsule.id, user.id, draft.trim())
      setCapsule(updated)
      setDraft(updated.content || '')
    } catch (e) {
      setError(e.message || 'Could not save draft.')
    } finally {
      setSaving(false)
    }
  }

  const handleWithdraw = async () => {
    if (!capsule || !user) return
    if (!window.confirm('Withdraw this capsule? This permanently deletes it.')) return

    try {
      await withdrawCapsule(capsule.id, user.id)
      navigate('/feed')
    } catch (e) {
      setError(e.message || 'Could not withdraw capsule.')
    }
  }

  if (loading) {
    return (
      <div className="workspace-page">
        <div className="workspace-loading"><span className="spinner" /></div>
      </div>
    )
  }

  if (!capsule) {
    return (
      <div className="workspace-page">
        <div className="workspace-empty">{error || 'Workspace unavailable.'}</div>
      </div>
    )
  }

  return (
    <div className="workspace-page">
      <header className="workspace-header">
        <button className="workspace-back-btn" onClick={() => navigate('/feed')}>← Back to feed</button>
        <div className="workspace-title-wrap">
          <div className="workspace-title">Incubation Workspace</div>
          <div className="workspace-subtitle">Thinking studio · publish in {timeLeft}</div>
        </div>
      </header>

      <main className="workspace-layout">
        <section className="workspace-main">
          <div className="workspace-card">
            <div className="workspace-card-header">
              <div className="workspace-card-title">Draft Studio</div>
              <div className="workspace-status-pill">Incubating ({timeLeft})</div>
            </div>

            <textarea
              className="workspace-editor"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={10000}
              rows={16}
            />

            {error && <div className="workspace-error">{error}</div>}

            <div className="workspace-actions">
              <span className="workspace-meta">{draft.length}/10000 · edited {iterations.length} times</span>
              <div className="workspace-action-buttons">
                <button className="btn btn-ghost" onClick={handleWithdraw}>Withdraw</button>
                <button className="btn btn-primary" disabled={!hasMeaningfulChange || saving} onClick={saveDraft}>
                  {saving ? <span className="spinner" /> : 'Save Revision'}
                </button>
              </div>
            </div>
          </div>

          <div className="workspace-card">
            <div className="workspace-card-title">Draft Evolution</div>
            <p className="workspace-note">You edited this {iterations.length} time{iterations.length === 1 ? '' : 's'} during incubation.</p>

            <div className="workspace-changes">
              <div className="workspace-subheading">Changed sentences</div>
              {changed.length === 0 ? (
                <div className="workspace-muted">No new sentence-level changes yet.</div>
              ) : (
                changed.map((sentence, idx) => (
                  <div key={`${idx}-${sentence.slice(0, 12)}`} className="workspace-change-row">
                    {sentence}
                  </div>
                ))
              )}
            </div>

            <div className="workspace-versions">
              <div className="workspace-subheading">Version history</div>
              {iterations.length === 0 ? (
                <div className="workspace-muted">No prior versions yet.</div>
              ) : (
                [...iterations].reverse().map((it, idx) => (
                  <div key={`${it.updated_at}-${idx}`} className="workspace-version-row">
                    <span className="workspace-version-tag">v{iterations.length - idx}</span>
                    <span className="workspace-version-time">{new Date(it.updated_at).toLocaleString()}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        <aside className="workspace-side">
          <div className="workspace-card">
            <div className="workspace-card-title">Guided Reflection</div>
            <div className="workspace-prompt">{REFLECTION_PROMPTS[promptIndex]}</div>
          </div>

          <div className="workspace-card">
            <div className="workspace-card-title">Automatic Counterarguments</div>
            {counterarguments.map((point, idx) => (
              <div key={idx} className="workspace-bullet">{point}</div>
            ))}
          </div>

          <div className="workspace-card">
            <div className="workspace-card-title">Related Research</div>
            {researchLinks.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="workspace-link"
              >
                {link.title} →
              </a>
            ))}
          </div>

          <div className="workspace-card">
            <div className="workspace-card-title">Past Agora Posts</div>
            {relatedCapsules.length === 0 ? (
              <div className="workspace-muted">No closely related capsules found yet.</div>
            ) : (
              relatedCapsules.map((item) => (
                <button
                  key={item.id}
                  className="workspace-related"
                  onClick={() => navigate(`/capsule/${item.id}`)}
                >
                  {item.content.slice(0, 120)}{item.content.length > 120 ? '…' : ''}
                </button>
              ))
            )}
          </div>
        </aside>
      </main>
    </div>
  )
}
